<template>
  <div class="legal-page">
    <PageBanner
      title="Cookie Policy"
      subtitle="What we store on your device, why we store it, and how to change your mind."
    />

    <LegalSection updated="28 July 2026">
      <div class="callout">
        <p>
          You can review or change your choice at any time.
          <button type="button" class="inline-trigger" @click="openCookiePreferences">
            Open cookie preferences
          </button>
        </p>
      </div>

      <h2>1. What cookies are</h2>
      <p>
        Cookies are small text files placed on your device when you visit a
        website. Similar technologies such as local storage work the same way.
        They let a site remember things between page loads, for example the
        cookie choice you have already made.
      </p>

      <h2>2. Our approach</h2>
      <p>
        This site loads no optional cookies until you allow them. When you first
        arrive you see a notice with three options: accept all, reject optional,
        or manage each category individually. Rejecting optional cookies does
        not restrict any part of the site apart from the embedded map.
      </p>

      <h2>3. Categories we use</h2>
      <table>
        <thead>
          <tr>
            <th>Category</th>
            <th>What it does</th>
            <th>Set by</th>
            <th>Typical duration</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Strictly necessary</td>
            <td>
              Remembers your cookie choice so the notice is not shown on every
              visit. Stored in your browser's local storage under
              <code>cpph_cookie_consent</code>.
            </td>
            <td>This website</td>
            <td>Until you clear it</td>
          </tr>
          <tr>
            <td>Analytics</td>
            <td>
              Would tell us which pages and projects visitors view most, so we
              can improve the site. No analytics tool is currently installed.
              Nothing is loaded unless you opt in and we later add one.
            </td>
            <td>This website or its analytics provider</td>
            <td>Up to 24 months</td>
          </tr>
          <tr>
            <td>Maps and embedded content</td>
            <td>
              Loads the Google Maps view of our office on the Contact page.
              Google may set its own cookies to deliver and secure that content.
            </td>
            <td>Google</td>
            <td>Set by Google, up to 24 months</td>
          </tr>
        </tbody>
      </table>

      <h2>4. Cookies we do not use</h2>
      <p>
        We do not run advertising or retargeting pixels, we do not use social
        media tracking scripts, and we do not share cookie data with data
        brokers.
      </p>

      <h2>5. Managing your choice</h2>
      <p>
        Use the Cookie settings link in the footer, or the button at the top of
        this page, to reopen the preferences panel at any time. You can also
        clear cookies and site data through your browser settings, which will
        reset your choice and bring the notice back on your next visit.
      </p>
      <p>
        If we change the categories, we ask for your choice again rather than
        assuming your earlier answer still applies.
      </p>

      <h2>6. More information</h2>
      <p>
        How we handle the information you send us is explained in our
        <router-link to="/privacy-policy">Privacy Policy</router-link>.
      </p>
    </LegalSection>
  </div>
</template>

<script setup>
import PageBanner from '@/components/PageBanner.vue';
import LegalSection from '@/components/LegalSection.vue';
import { openCookiePreferences } from '@/utils/consent';
</script>

<style scoped>
.legal-page {
  overflow-x: hidden;
}

.inline-trigger {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: #b91c1c;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
}

.inline-trigger:hover {
  color: #911515;
}
</style>
